import emoji from 'emoji-dictionary';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import htmlParser from 'react-markdown/plugins/html-parser';
import ReactMarkdown from 'react-markdown/with-html';
import removeMd from 'remove-markdown';

import styles from '../../css/markdown.module.css';

import CodeBlock from '@components/CodeBlock';
import CustomHead from '@components/CustomHead';
import Footer from '@components/FooterNew';
import Header from '@components/HeaderNew';
import HeadingRenderer from '@components/HeadingRenderer';
import { Arrow } from '@components/Icons';
import LearnSideCTA from '@components/LearnSideCTA';
import { getDbtTutorialPosts } from '@utils/contentfulClient';

const FooterNavigation = ({ tutorials, currentSlug }) => {
  const currentIndex = tutorials.map((t) => t.fields.slug).indexOf(currentSlug);
  const prevTutorial = currentIndex === 0 ? null : tutorials[currentIndex - 1];
  const nextTutorial =
    currentIndex === tutorials.length ? null : tutorials[currentIndex + 1];

  return (
    <div className="flex justify-between mt-20">
      <div>
        {prevTutorial && (
          <>
            <p className="text-sm text-gray-500 mb-2">Previous</p>
            <Link href={prevTutorial.fields.slug}>
              <a className="flex flex-no-wrap items-center text-blue-100 hover:text-blue-200 font-medium transition-colors duration-200 ease-in-out">
                <Arrow color="currentColor" className="mr-2" />{' '}
                {prevTutorial.fields.title}
              </a>
            </Link>
          </>
        )}
      </div>
      <div>
        {nextTutorial && (
          <>
            <p className="text-sm text-gray-500 mb-2">Next</p>
            <Link href={nextTutorial.fields.slug}>
              <a className="flex flex-no-wrap items-center justify-end text-blue-100 hover:text-blue-200 font-medium transition-colors duration-200 ease-in-out">
                {nextTutorial.fields.title}
                <Arrow
                  color="currentColor"
                  className="ml-2 transform rotate-180"
                />
              </a>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

const getRandomCTA = () => {
  const list = [
    {
      id: 1,
      title: 'The first SQL editor with built-in dbt Core ™',
      description:
        'Simplified dbt development - Create and manage your dbt models and macros all in one place with PopSQL',
    },
    {
      id: 2,
      title: 'Streamline your SQL workflow using dbt Macros',
      description: 'Eliminate repetitive SQL tasks with PopSQL and dbt Macros',
    },
    {
      id: 3,
      title:
        'Supercharge your dbt workflow: Code editor, terminal, and SQL editor in one',
      description:
        'Manage your entire dbt workflow within PopSQL and minimize context switching',
    },
    {
      id: 4,
      title: 'Unified workspace for your dbt workflow',
      description:
        'Forget about the painful parts of dbt development, focus on what matters the most - data analysis',
    },
  ];
  const random = list[Math.floor(Math.random() * list.length)];

  return random;
};

const DbtTutorialPage = ({
  tutorialsList,
  tutorialsWithSections,
  tutorial,
}) => {
  const router = useRouter();
  const getSummary = (source) => {
    const stripped = removeMd(source, {
      stripListLeaders: true,
      listUnicodeChar: '',
      gfm: true,
    });
    const lines = stripped.split('\n');

    // print the first few lines...
    return `${lines[0]} ${lines[1]} ${lines[2]} ${lines[3]}`;
  };

  const cleanPost = {
    metaTags: tutorial.fields.metaTags?.fields,
    title: tutorial.fields.title,
    body: tutorial.fields.body,
    summary: getSummary(tutorial.fields.body),
  };

  const parseHtml = htmlParser({
    isValidNode: (node) => node.type !== 'script',
  });

  const emojiSupport = (text) =>
    text.value.replace(/:\w+:/gi, (name) => emoji.getUnicode(name));

  const sections = Object.keys(tutorialsWithSections);
  const metaTitle = cleanPost.metaTags?.title || `${cleanPost.title} - PopSQL`;
  const metaDesc = cleanPost.metaTags?.description || cleanPost.summary;
  const randomCTA = getRandomCTA();
  const ctaUrl =
    'https://n811kmuga5v.typeform.com/to/lCRSZYeC?utm_source=learndbt';

  return (
    <>
      <CustomHead
        meta={{
          title: metaTitle,
          description: metaDesc,
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/og/dbt-tutorials.jpg`,
          url: `${process.env.NEXT_PUBLIC_WEB_HOST}${router.asPath}`,
        }}
        openGraph={{
          title: metaTitle,
          description: metaDesc,
          url: `${process.env.NEXT_PUBLIC_WEB_HOST}${router.asPath}`,
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/og/dbt-tutorials.jpg`,
        }}
      />
      <main>
        <Header />
        <section className="grid gap-10 lg:grid-cols-[200px_1fr_200px] max-w-[1200px] mx-auto py-12 md:py-20 px-6 md:px-12">
          <aside className="hidden lg:block">
            {sections.map((type, index) => (
              <div
                key={index}
                className={`${
                  tutorialsWithSections[type]?.length < 1 && 'hidden'
                } mb-8`}
              >
                <p className="text-gray-500 mt-2 mb-2 opacity-60 text-xs tracking-wider font-semibold font-inter uppercase">
                  {type}
                </p>
                <ul>
                  {tutorialsWithSections[type].map((tut) => (
                    <li key={tut.sys.id} className="my-2">
                      <Link href={tut.fields.slug}>
                        <a
                          className={`${
                            tutorial.fields.slug === tut.fields.slug
                              ? 'text-blue-100'
                              : 'text-black'
                          } hover:text-blue-100 cursor-pointer text-[0.9rem] font-inter`}
                        >
                          {tut.fields.title}
                        </a>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </aside>
          <div className="dbt-tutorial max-w-[38rem]">
            <Link href="/learn-dbt">
              <a className="flex flex-no-wrap items-center text-gray-600 mb-6">
                <Arrow color="currentColor" className="mr-2" /> Back to
                tutorials
              </a>
            </Link>
            <ReactMarkdown
              className={styles.popsql__markdown}
              source={cleanPost.body}
              escapeHtml={false}
              renderers={{
                code: CodeBlock,
                heading: HeadingRenderer,
                text: emojiSupport,
              }}
              astPlugins={[parseHtml]}
            />
            <FooterNavigation
              tutorials={tutorialsList}
              currentSlug={tutorial.fields.slug}
            />
          </div>

          <div>
            <LearnSideCTA
              cta={{
                text: 'Join the beta',
                href: ctaUrl,
              }}
              className=""
              title={randomCTA.title}
              description={randomCTA.description}
              image={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/features/dbt/dbt_logo.jpg`}
            />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export async function getStaticPaths() {
  const tutorialsList = await getDbtTutorialPosts();
  const paths = [];
  tutorialsList.forEach((t) => paths.push(t.fields.slug));

  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps(ctx) {
  const { slug } = ctx.params;
  const tutorialsList = await getDbtTutorialPosts();
  const filteredTutorial = tutorialsList.filter(
    (t) => t.fields.slug === `/learn-dbt/${slug}`,
  );

  const tutorialsWithSections = {
    Basics: [],
    'Building projects': [],
    Integration: [],
    Deployment: [],
    Github: [],
  };

  // sort tutorials into each category
  tutorialsList.forEach((tut) => {
    const types = tut.fields.type as string[];
    types.forEach((type) => tutorialsWithSections[type]?.push(tut));
  });

  return {
    props: {
      tutorial: filteredTutorial[0],
      tutorialsWithSections,
      tutorialsList,
    },
  };
}

export default DbtTutorialPage;
